import React, { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import MobileDialog from './MobileDialog';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PrintIcon from '@mui/icons-material/Print';
import API from '../../services/api';
import { generateBusinessHeader, openPrintWindow } from '../../utils/printUtils';

const HEBREW_MONTHS = [
  'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
];

const TYPE_LABELS = { Payment: 'תשלום', Charge: 'חיוב', Credit: 'זיכוי' };
const TYPE_COLORS = {
  Payment: '#e8f5e9',
  Charge: '#ffebee',
  Credit: '#fff8e1',
};

function formatCurrencyIL(num) {
  return parseFloat(num || 0).toLocaleString('he-IL', { style: 'currency', currency: 'ILS' });
}

function formatDateIL(isoStr) {
  if (!isoStr) return '';
  const [y, m, d] = isoStr.split('-');
  return `${d}.${m}.${y}`;
}

function toLocalISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function generatePrintHTML(data, uiConfig, filter, periodLabel, businessProfile = {}) {
  const debtSchema = uiConfig?.debt_fields_schema || {};
  const clientFieldsSchema = uiConfig?.client_fields_schema || {};
  const orderTransactionSchema = uiConfig?.order_transaction_fields_schema || {};
  const generatedDate = new Date().toLocaleDateString('he-IL');

  const resolveSubTypeLabel = (subType) => {
    return debtSchema[subType]?.label
      || clientFieldsSchema[subType]?.label
      || orderTransactionSchema[subType]?.label
      || subType
      || '';
  };

  const txRows = data.transactions.map(tx => {
    const isPaymentOrCredit = tx.Type === 'Payment' || tx.Type === 'Credit';
    const amountDisplay = isPaymentOrCredit
      ? `${formatCurrencyIL(tx.Amount)}-`
      : `${formatCurrencyIL(tx.Amount)}+`;
    const rowBg = TYPE_COLORS[tx.Type] || '#fff';
    const hasFormattedNote = tx.Note && tx.Note.includes('================');
    const noteCell = hasFormattedNote
      ? '<td class="note-cell"><span class="compact-note">ראה פרטים למטה</span></td>'
      : `<td class="note-cell">${tx.Note || ''}</td>`;
    const formattedNoteRow = hasFormattedNote
      ? `<tr><td colspan="5" style="padding:0"><div class="formatted-note">${tx.Note}</div></td></tr>`
      : '';
    return `<tr style="background-color:${rowBg}">
      <td>${formatDateIL(tx.ActualDate)}</td>
      <td>${TYPE_LABELS[tx.Type] || tx.Type}</td>
      <td>${resolveSubTypeLabel(tx.SubType)}</td>
      <td dir="ltr" style="text-align:center">${amountDisplay}</td>
      ${noteCell}
    </tr>${formattedNoteRow}`;
  }).join('');

  const noTxRow = `<tr><td colspan="5" style="text-align:center;color:#888;padding:16px">אין תנועות בתקופה זו</td></tr>`;

  const heroColor = '#1a1a1a';
  const heroBg = '#f5f5f5';

  const heroLabel = filter === 'charges' ? 'סה"כ חיובים לתקופה' : 'שינוי יתרה לתקופה';

  const paymentsRow = filter !== 'charges'
    ? '<tr><td>סה"כ תשלומים</td><td dir="ltr" style="color:#2e7d32;font-weight:bold">' + formatCurrencyIL(data.transaction_summary.total_payments) + '</td></tr>'
    : '';

  const creditsRow = filter !== 'charges' && data.transaction_summary.total_credits > 0
    ? '<tr><td>סה"כ זיכויים</td><td dir="ltr" style="color:#f57f17;font-weight:bold">' + formatCurrencyIL(data.transaction_summary.total_credits) + '</td></tr>'
    : '';

  const beforeAfterSection = filter !== 'charges'
    ? '<div class="before-after">יתרה בתחילת התקופה: <strong>' + formatCurrencyIL(data.opening_balance) + '</strong>&nbsp;&nbsp;←&nbsp;&nbsp;יתרה בסוף התקופה: <strong>' + formatCurrencyIL(data.closing_balance) + '</strong></div>'
    : '';

  return `<!DOCTYPE html>
<html dir="rtl">
<head>
  <meta charset="utf-8">
  <title>דוח חודשי - ${data.client.ClientName} - ${periodLabel}</title>
  <style>
    @media print {
      @page { margin: 10mm; }
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
    }
    body {
      direction: rtl;
      font-family: Arial, sans-serif;
      font-size: 16px;
      margin: 0;
      padding: 16px;
    }
    h1 { font-size: 26px; margin: 0 0 6px; text-align: center; }
    h2 { font-size: 20px; margin: 20px 0 8px; border-bottom: 2px solid #ddd; padding-bottom: 4px; color: #444; }
    .meta { text-align: center; color: #555; font-size: 14px; margin-bottom: 16px; }
    table { border-collapse: collapse; width: 100%; margin-bottom: 16px; }
    th, td { border: 1px solid #ccc; padding: 8px; text-align: center; vertical-align: middle; }
    th { background-color: #f0f0f0; font-weight: bold; }
    .hero {
      text-align: center;
      margin: 16px 0 24px;
    }
    .hero-box {
      display: inline-block;
      border: 3px solid ${heroColor};
      border-radius: 10px;
      padding: 14px 40px;
      background: ${heroBg};
    }
    .hero-label { font-size: 14px; color: #666; margin-bottom: 6px; }
    .hero-amount { font-size: 22px; font-weight: 600; color: ${heroColor}; direction: ltr; }
    .summary-table { width: auto; min-width: 260px; }
    .summary-table td { min-width: 130px; }
    .before-after {
      text-align: center;
      margin-top: 20px;
      font-size: 15px;
      color: #555;
      padding: 10px;
      background: #f9f9f9;
      border-radius: 6px;
      border: 1px solid #e0e0e0;
    }
    .note-cell {
      max-width: 300px;
      text-align: right !important;
      vertical-align: top;
      word-break: break-word;
    }
    .formatted-note {
      font-family: monospace;
      white-space: pre-wrap;
      font-size: 12px;
      line-height: 1.4;
      padding: 8px;
      background-color: #f9f9f9;
      border: 1px solid #e0e0e0;
      text-align: right;
      direction: rtl;
    }
    .compact-note { font-style: italic; color: #666; }
  </style>
</head>
<body>
  ${generateBusinessHeader(businessProfile)}
  <h1>דוח חודשי</h1>
  <div style="text-align:center; margin-bottom: 4px;">
    <span style="font-size:22px; font-weight:bold;">${data.client.ClientName}</span>
  </div>
  <div class="meta">
    ${data.client.ClientBusinessID}
    &nbsp;|&nbsp; תקופה: ${periodLabel}
    &nbsp;|&nbsp; הופק: ${generatedDate}
  </div>

  <div class="hero">
    <div class="hero-box">
      <div class="hero-label">${heroLabel}</div>
      <div class="hero-amount">${formatCurrencyIL(data.transaction_summary.monthly_balance)}</div>
    </div>
  </div>

  <h2>תנועות</h2>
  <table>
    <thead>
      <tr>
        <th>תאריך</th>
        <th>סוג</th>
        <th>קטגוריה</th>
        <th>סכום</th>
        <th>הערה</th>
      </tr>
    </thead>
    <tbody>
      ${data.transactions.length ? txRows : noTxRow}
    </tbody>
  </table>

  <h2>סיכום</h2>
  <table class="summary-table" style="margin: 0 auto 16px;">
    <tbody>
      <tr>
        <td>סה"כ חיובים</td>
        <td dir="ltr" style="color:#c62828;font-weight:bold">${formatCurrencyIL(data.transaction_summary.total_charges)}</td>
      </tr>
      ${paymentsRow}
      ${creditsRow}
    </tbody>
  </table>

  ${beforeAfterSection}
</body>
</html>`;
}

const MonthlyStatementDialog = ({ open, onClose, clientBusinessId, clientName, uiConfig, businessProfile = {} }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [statementData, setStatementData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [error, setError] = useState('');
  const [txFilter, setTxFilter] = useState('charges');
  const [rangeMode, setRangeMode] = useState('month');
  const [rangeStart, setRangeStart] = useState(new Date());
  const [rangeEnd, setRangeEnd] = useState(new Date());

  useEffect(() => {
    if (open) {
      setStatementData(null);
      setError('');
      setSelectedDate(new Date());
      setTxFilter('charges');
      setRangeMode('month');
      setRangeStart(new Date());
      setRangeEnd(new Date());
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (rangeMode === 'month' && !selectedDate) return;
    if (rangeMode === 'range' && (!rangeStart || !rangeEnd || rangeStart > rangeEnd)) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setStatementData(null);
    const params = rangeMode === 'month'
      ? { business_id: clientBusinessId, year: selectedDate.getFullYear(), month: selectedDate.getMonth() + 1 }
      : { business_id: clientBusinessId, start_date: toLocalISODate(rangeStart), end_date: toLocalISODate(rangeEnd) };
    API.get('/client/monthly-statement/', { params })
      .then(res => { if (!cancelled) setStatementData(res.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.error || 'שגיאה בטעינת הדוח'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, selectedDate, clientBusinessId, rangeMode, rangeStart, rangeEnd]);

  const handlePrint = () => {
    if (!statementData) return;
    setPrintLoading(true);
    const printTxs = txFilter === 'charges'
      ? statementData.transactions.filter(tx => tx.Type === 'Charge')
      : statementData.transactions;
    const printCharges = printTxs.reduce((s, tx) => s + parseFloat(tx.Amount || 0), 0);
    const printSummary = txFilter === 'charges'
      ? { total_charges: printCharges, total_payments: 0, total_credits: 0, monthly_balance: printCharges }
      : statementData.transaction_summary;
    const printData = { ...statementData, transactions: printTxs, transaction_summary: printSummary };
    const html = generatePrintHTML(printData, uiConfig, txFilter, periodLabel, businessProfile);
    openPrintWindow(html);
    setPrintLoading(false);
  };

  const handleClose = () => {
    if (!loading && !printLoading) onClose();
  };

  const resolveSubTypeLabel = (subType) => {
    const debtSchema = uiConfig?.debt_fields_schema || {};
    const clientFieldsSchema = uiConfig?.client_fields_schema || {};
    const orderTransactionSchema = uiConfig?.order_transaction_fields_schema || {};
    return debtSchema[subType]?.label
      || clientFieldsSchema[subType]?.label
      || orderTransactionSchema[subType]?.label
      || subType
      || '';
  };

  const periodLabel = rangeMode === 'month'
    ? (selectedDate ? `${HEBREW_MONTHS[selectedDate.getMonth()]} ${selectedDate.getFullYear()}` : '')
    : (rangeStart && rangeEnd ? `${formatDateIL(toLocalISODate(rangeStart))} - ${formatDateIL(toLocalISODate(rangeEnd))}` : '');

  const filteredTransactions = statementData
    ? (txFilter === 'charges'
        ? statementData.transactions.filter(tx => tx.Type === 'Charge')
        : statementData.transactions)
    : [];

  const filteredSummary = statementData
    ? (txFilter === 'charges'
        ? (() => {
            const total = filteredTransactions.reduce((s, tx) => s + parseFloat(tx.Amount || 0), 0);
            return { total_charges: total, total_payments: 0, total_credits: 0, monthly_balance: total };
          })()
        : statementData.transaction_summary)
    : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
      <MobileDialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.4rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, direction: 'ltr' }}>
            <CalendarMonthIcon sx={{ color: 'secondary.main' }} />
            דוח חודשי
          </Box>
          <IconButton onClick={handleClose} disabled={loading || printLoading} sx={{ color: 'grey.500' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2, overflowY: 'auto' }}>
          {/* Client info */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">לקוח</Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{clientName}</Typography>
          </Box>

          {/* Controls */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            <RadioGroup
              row
              value={rangeMode}
              onChange={(e) => setRangeMode(e.target.value)}
            >
              <FormControlLabel value="month" control={<Radio size="small" />} label="חודש" />
              <FormControlLabel value="range" control={<Radio size="small" />} label="טווח תאריכים" />
            </RadioGroup>
            {rangeMode === 'month' ? (
              <DatePicker
                label="בחר חודש"
                value={selectedDate}
                onChange={(val) => setSelectedDate(val)}
                views={['year', 'month']}
                openTo="month"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { '& .MuiOutlinedInput-root': { direction: 'ltr' } },
                  }
                }}
              />
            ) : (
              <>
                <DatePicker
                  label="מתאריך"
                  value={rangeStart}
                  onChange={(val) => setRangeStart(val)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { '& .MuiOutlinedInput-root': { direction: 'ltr' } },
                    }
                  }}
                />
                <DatePicker
                  label="עד תאריך"
                  value={rangeEnd}
                  onChange={(val) => setRangeEnd(val)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { '& .MuiOutlinedInput-root': { direction: 'ltr' } },
                    }
                  }}
                />
              </>
            )}
            <RadioGroup
              row
              value={txFilter}
              onChange={(e) => setTxFilter(e.target.value)}
            >
              <FormControlLabel value="charges" control={<Radio size="small" />} label="חיובים בלבד" />
              <FormControlLabel value="all" control={<Radio size="small" />} label="כל התנועות" />
            </RadioGroup>
            {loading && <CircularProgress size={24} color="secondary" />}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
          )}

          {/* Statement Preview */}
          {statementData && (
            <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, overflow: 'hidden' }}>
              {/* Header */}
              <Box sx={{ bgcolor: 'secondary.main', color: 'white', p: 2, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>דוח חודשי — {periodLabel}</Typography>
                <Typography variant="body2">{clientName} ({clientBusinessId})</Typography>
              </Box>

              <Box sx={{ p: 2 }}>
                {/* Transactions table */}
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>תנועות</Typography>
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>תאריך</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>סוג</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>קטגוריה</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>סכום</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>הערה</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTransactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                          אין תנועות בתקופה זו
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTransactions.map((tx) => {
                        const isPaymentOrCredit = tx.Type === 'Payment' || tx.Type === 'Credit';
                        return (
                          <TableRow key={tx.TrID} sx={{ bgcolor: TYPE_COLORS[tx.Type] || '#fff' }}>
                            <TableCell align="center">{formatDateIL(tx.ActualDate)}</TableCell>
                            <TableCell align="center">{TYPE_LABELS[tx.Type] || tx.Type}</TableCell>
                            <TableCell align="center">{resolveSubTypeLabel(tx.SubType)}</TableCell>
                            <TableCell align="center" dir="ltr">
                              {isPaymentOrCredit
                                ? `${formatCurrencyIL(tx.Amount)}-`
                                : `${formatCurrencyIL(tx.Amount)}+`}
                            </TableCell>
                            <TableCell align="center">{tx.Note || ''}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>

                {/* Hero: monthly balance change */}
                <Box sx={{ textAlign: 'center', my: 2 }}>
                  <Box sx={{
                    display: 'inline-block',
                    border: '2px solid',
                    borderColor: 'text.primary',
                    borderRadius: 2,
                    px: 4, py: 1.5,
                    bgcolor: 'grey.50',
                  }}>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {txFilter === 'charges' ? 'סה"כ חיובים לתקופה' : 'שינוי יתרה לתקופה'}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', direction: 'ltr' }}>
                      {formatCurrencyIL(filteredSummary?.monthly_balance || 0)}
                    </Typography>
                  </Box>
                </Box>

                {/* Summary chips */}
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>סיכום</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-start', mb: 2 }}>
                  {filteredSummary?.total_charges > 0 && (
                    <Chip label={`חיובים: ${formatCurrencyIL(filteredSummary.total_charges)}`} sx={{ bgcolor: '#ffebee', border: '1px solid #ef9a9a' }} size="small" />
                  )}
                  {filteredSummary?.total_payments > 0 && (
                    <Chip label={`תשלומים: ${formatCurrencyIL(filteredSummary.total_payments)}`} sx={{ bgcolor: '#e8f5e9', border: '1px solid #a5d6a7' }} size="small" />
                  )}
                  {filteredSummary?.total_credits > 0 && (
                    <Chip label={`זיכויים: ${formatCurrencyIL(filteredSummary.total_credits)}`} sx={{ bgcolor: '#fff8e1', border: '1px solid #ffe082' }} size="small" />
                  )}
                </Box>

                {/* Before → After subtle line — hidden in charges-only mode */}
                {txFilter !== 'charges' && (
                  <Box sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200', borderRadius: 1, p: 1.5 }}>
                    <Typography variant="body2" color="text.secondary" textAlign="right">
                      יתרה בתחילת התקופה: <strong>{formatCurrencyIL(statementData.opening_balance)}</strong>
                      {'  ←  '}
                      יתרה בסוף התקופה: <strong>{formatCurrencyIL(statementData.closing_balance)}</strong>
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleClose} disabled={loading || printLoading} size="large">
            סגור
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePrint}
            disabled={!statementData || printLoading || loading}
            startIcon={printLoading ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}
            size="medium"
            sx={{ direction: 'ltr' }}
          >
            {printLoading ? 'מכין...' : 'הדפסה'}
          </Button>
        </DialogActions>
      </MobileDialog>
    </LocalizationProvider>
  );
};

export default MonthlyStatementDialog;
